import React from 'react';
import { ConversionFunnel as ConversionFunnelType } from '@/types';
import { cn, formatPercentage } from '@/utils';

interface ConversionFunnelProps {
  funnel: ConversionFunnelType;
}

export const ConversionFunnel: React.FC<ConversionFunnelProps> = ({ funnel }) => {
  const maxCount = Math.max(...funnel.stages.map(stage => stage.count));

  const getHealthColor = (conversionRate: number, isFirst: boolean = false) => {
    if (isFirst) return 'bg-blue-500';
    if (conversionRate >= 70) return 'bg-emerald-500';
    if (conversionRate >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getHealthStatus = () => {
    const avgConversion = funnel.stages.slice(1).reduce((sum, stage) => sum + stage.conversionRate, 0) / (funnel.stages.length - 1);
    if (avgConversion >= 60) return { color: 'text-emerald-400', status: 'Healthy conversion rates' };
    if (avgConversion >= 40) return { color: 'text-amber-400', status: 'Needs attention' };
    return { color: 'text-red-400', status: 'Critical conversion rates' };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-50">
          Weekly Conversion Funnel
        </h2>
        <select className="bg-slate-800 border border-slate-600 text-slate-300 text-sm rounded px-3 py-1">
          <option>This Week</option>
          <option>Last Week</option>
          <option>This Month</option>
        </select>
      </div>

      {/* Funnel visualization */}
      <div className="space-y-4 mb-6">
        {funnel.stages.map((stage, index) => {
          const width = (stage.count / maxCount) * 100;
          const isFirst = index === 0;
          
          return (
            <div key={stage.name} className="flex items-center space-x-4">
              <div className="w-24 text-right">
                <div className="text-sm font-medium text-slate-300">
                  {stage.name}
                </div>
              </div>
              
              <div className="flex-1 relative">
                <div className="bg-slate-600 rounded-full h-8 relative overflow-hidden">
                  <div 
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      getHealthColor(stage.conversionRate, isFirst)
                    )}
                    style={{ width: `${width}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {stage.count.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="w-16 text-left">
                {!isFirst && (
                  <div className="text-sm text-slate-300">
                    {formatPercentage(stage.conversionRate)}
                  </div>
                )}
              </div>
              
              {index < funnel.stages.length - 1 && (
                <div className="text-slate-400 text-lg">
                  →
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Health indicator */}
      <div className="flex items-center space-x-2 mb-4">
        <div className="flex space-x-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div 
              key={i}
              className={cn(
                'w-3 h-2 rounded-sm',
                i < 7 ? healthStatus.color.replace('text-', 'bg-') : 'bg-slate-600'
              )}
            />
          ))}
        </div>
        <span className={cn('text-sm', healthStatus.color)}>
          {healthStatus.status}
        </span>
      </div>

      {/* Drop-off analysis */}
      <div className="border-t border-slate-600 pt-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3">
          Drop-off Analysis:
        </h3>
        <ul className="space-y-1 text-sm text-slate-400">
          {funnel.stages.slice(1).map((stage, index) => 
            stage.dropoffReasons?.map((reason, reasonIndex) => (
              <li key={`${index}-${reasonIndex}`} className="flex items-start">
                <span className="text-slate-500 mr-2">•</span>
                {reason}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};